// ============================================================================
// lib/remote/contractEndpoint.ts — эндпоинт сеанса «Удалённого доступа»:
// адрес хоста РЕГИСТРИРУЕТСЯ на платформе и РЕЗОЛВИТСЯ по sessionId — ручной
// ввод адреса убран, источник адреса ЕДИНСТВЕННЫЙ (сервер, SSoT). Транспорт:
//   • 'contract' — прямой адрес WS-сервера хоста (локальная сеть);
//   • 'relay'    — РЕЛЕЙ сервера за NAT: хост и зритель соединяются
//                  ИСХОДЯЩИМИ к серверу, входящие порты не нужны.
// Ошибки — явные (без фоллбэков).
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { friendlyError } from '@/lib/errors';
import { logEvent } from '@/lib/eventLog';

// Транспорты эндпоинта сеанса (сервер валидирует значение).
export const CONTRACT_TRANSPORT = 'contract';
export const RELAY_TRANSPORT = 'relay';

export interface RemoteEndpoint {
  url: string;
  transport: string;
}

/** Резолв адреса сеанса (RemoteService.GetRemoteEndpoint): URL + транспорт. */
export async function fetchRemoteEndpoint(sessionId: string): Promise<RemoteEndpoint> {
  try {
    const info = await getSDK().remote.getRemoteEndpoint({ sessionId });
    const url = info.endpointUrl;
    const transport = info.transport || CONTRACT_TRANSPORT;
    if (!url) {
      throw new Error('сервер не вернул адрес сеанса — повторите попытку');
    }
    void logEvent({
      level: 'info',
      category: 'ui',
      message: 'удалённый доступ (контракт): адрес сеанса получен с платформы',
      entityType: 'remote',
      entityCode: 'session',
      objectId: sessionId,
      details: `${transport} ${url}`,
    });
    return { url, transport };
  } catch (e: unknown) {
    void logEvent({
      level: 'error',
      category: 'ui',
      message: 'удалённый доступ (контракт): адрес сеанса не получен',
      entityType: 'remote',
      entityCode: 'session',
      objectId: sessionId,
      details: friendlyError(e),
    });
    throw e;
  }
}

/** Регистрация адреса сеанса на платформе (upsert по сеансу). */
export async function registerRemoteEndpointOnPlatform(
  sessionId: string,
  endpointUrl: string,
  transport: string = CONTRACT_TRANSPORT,
): Promise<void> {
  await getSDK().remote.registerRemoteEndpoint({
    sessionId,
    endpointUrl,
    transport,
  });
}

export type RelayRole = 'host' | 'viewer';

/** Адрес подключения к релею: relay_url + роль + токен (query сохраняется). */
export function relaySocketUrl(relayUrl: string, role: RelayRole, token: string): string {
  const value = relayUrl.trim();
  if (!value) throw new Error('Сервер не вернул адрес релея — повторите попытку');
  if (!token) throw new Error('Сервер не вернул токен релея — повторите попытку');
  const sep = value.includes('?') ? '&' : '?';
  return `${value}${sep}role=${role}&token=${encodeURIComponent(token)}`;
}

/** Токен роли для релея (RemoteService.IssueRelayToken): {token, relayUrl}. */
export async function issueRelayToken(
  sessionId: string,
  role: RelayRole,
): Promise<{ token: string; relayUrl: string }> {
  const resp = await getSDK().remote.issueRelayToken({ sessionId, role });
  const relayUrl = resp.relayUrl;
  const token = resp.token;
  if (!relayUrl || !token) {
    throw new Error('Сервер не вернул данные релея (адрес/токен) — повторите попытку');
  }
  return { token, relayUrl };
}

